import React from 'react';
import { useState, useEffect, useLayoutEffect } from 'react';
import './App.css';

import {
    Outlet,
    Route,
    Routes,
    Navigate,
    useSearchParams,
    useLocation,
} from 'react-router';

import CssBaseline from '@mui/material/CssBaseline';
import { useTheme } from '@mui/material/styles';
import { Box, Typography } from '@mui/material';
import { I18nextProvider } from 'react-i18next';
import i18n from './context/language/i18n';
import DatePickerLocaleProvider from './context/language/DatePickerLocaleProvider';

import { ColorModeProvider } from './context/colorMode';
import { LevelsProvider } from './context/levels';

import {
    isDrawerOpen,
    drawerMaxWidth,
    drawerMinWidth,
} from './components/menus/def';
import { Header } from './components/header';
import { Content } from './components/content';
import { Footer } from './components/footer';

import {
    Auth,
    SignIn,
    SignUp,
    ResetPassword,
    Terms,
    Language,
    OrganizationSearch,
    OrganizationSearchDetail,
    OrganizationApply,
} from './components/auth';

import {
    ApplicationList,
    ApplicationDetail,
} from './pages/applications';
import { ContentsList, ContentsEdit } from './pages/contents';
import { DidimList, DidimEdit } from './pages/didims';
import { EventAdmin, EventAdminDetail, EventEdit, EventUser, EventUserDetail } from './pages/events';
import { GeneralList, GeneralEdit } from './pages/generals';
import { ReportActivity, ReportActivityMonthly, ReportStep, ReportStepUnion, ReportHeartbeat, ReportHeartbeatUnion, ReportDidim, ReportDidimMonthly } from './pages/reports';
import { RewardChallenge, RewardHistory } from './pages/rewards';
import { ChampionshipUser, ChampionshipUserDetail, ChampionshipUserAdd, ContentSelection, ChampionshipAdmin, ChampionshipAdminAdd } from './pages/championships';
import {
    OrganizationList,
    OrganizationDetail,
    OrganizationUpdate,
} from './pages/organizations';
import {
    UserList,
    UserEdit,
    UserDetail,
    UserMypage,
    UserMypageEdit,
    UserMain,
    UserExp,
    UserMembership,
    UserQr,
    UserPush
} from './pages/users';
import {
    NoticeAdmin,
    NoticeList,
    NoticeDetail,
    NoticeNotification,
} from './pages/notices';

import { useSelector } from 'react-redux';
import { selectAuth } from './redux/infoSlice';

import { isSupervisor, isAdmin, isUser } from './utils/base';
import { getMeInfoFromStorage, readStoredAuth } from './utils/authStorage';

/*
appType
  - admin : 관리자 앱 (default)
  - user : 유저 앱
*/

function App() {
    const [params] = useSearchParams();
    const location = useLocation();
    const authInfo = useSelector(selectAuth);

    // 웹과 앱 구분
    const userAgent = window.navigator.userAgent.toLowerCase();
    const isAndroid = userAgent.indexOf('android') > -1;
    const isIOS = /iphone|ipad|ipod/.test(userAgent);

    const [appType, setAppType] = useState(
        params.get('appType')
            ? params.get('appType')
            : isAndroid || isIOS
                ? 'user'
                : 'admin'
    );

    const [menusWidth, setMenusWidth] = useState(
        isDrawerOpen ? drawerMaxWidth : drawerMinWidth
    );
    const [pageWidth, setPageWidth] = useState(
        window.innerWidth - (isDrawerOpen ? drawerMaxWidth : drawerMinWidth)
    );
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    // toggleMenu 이벤트 리스닝 (menus 컴포넌트에서 메뉴 열기 요청 시)
    useEffect(() => {
        const handleToggleMenu = (e) => {
            if (e.detail?.open !== undefined) {
                setIsMenuOpen(e.detail.open);
            }
        };
        window.addEventListener('toggleMenu', handleToggleMenu);
        return () => {
            window.removeEventListener('toggleMenu', handleToggleMenu);
        };
    }, []);

    // const isHeaderRoute = !location.pathname.startsWith('/reports')
    //     && !location.pathname.startsWith('/championships/user-detail')
    //     && !location.pathname.startsWith('/championships/user-add')
    //     && !location.pathname.startsWith('/championships/content-selection');

    useEffect(() => {
        let appType_ = params.get('appType')
            ? params.get('appType')
            : localStorage.getItem('appType') || appType;
        localStorage.setItem('appType', appType_);
        setAppType(appType_);
    }, [appType, params]);

    // 유저 앱: 360×780 기준 vw/vh + 루트 rem (theme/didimUserViewport.css)
    useEffect(() => {
        const el = document.documentElement;
        if (appType === 'user') {
            el.classList.add('didim-user-app');
        } else {
            el.classList.remove('didim-user-app');
        }
        return () => el.classList.remove('didim-user-app');
    }, [appType]);

    return (
        <I18nextProvider i18n={i18n}>
            <DatePickerLocaleProvider>
                <ColorModeProvider appType={appType}>
                    <AppContent
                        appType={appType}
                        menusWidth={menusWidth}
                        setMenusWidth={setMenusWidth}
                        pageWidth={pageWidth}
                        setPageWidth={setPageWidth}
                        isMenuOpen={isMenuOpen}
                        setIsMenuOpen={setIsMenuOpen}
                        authInfo={authInfo}
                        location={location}
                    />
                </ColorModeProvider>
            </DatePickerLocaleProvider>
        </I18nextProvider>
    );
}

function AppContent({
    appType,
    menusWidth,
    setMenusWidth,
    pageWidth,
    setPageWidth,
    isMenuOpen,
    setIsMenuOpen,
    authInfo,
    location,
}) {
    const theme = useTheme();
    const [meInfo, setMeInfo] = useState(() => {
        const storedAuth = readStoredAuth();
        return storedAuth.isLoggedIn ? getMeInfoFromStorage() : null;
    });

    // 테마 모드에 따른 배경색 설정
    const backgroundColor = theme.palette.figma.mainBg;

    const isHeaderRoute = !location.pathname.startsWith('/championships/user-detail')
        && !location.pathname.startsWith('/championships/user-add')
        && !location.pathname.startsWith('/championships/content-selection');

    // #root와 body 요소에 배경색 동적 적용
    useEffect(() => {
        const rootElement = document.getElementById('root');
        const bodyElement = document.body;
        if (rootElement) {
            rootElement.style.backgroundColor = backgroundColor;
        }
        if (bodyElement) {
            bodyElement.style.backgroundColor = backgroundColor;
        }
    }, [backgroundColor]);

    const loadMeInfo = () => {
        setMeInfo(getMeInfoFromStorage());
    };

    useEffect(() => {
        loadMeInfo();
    }, [authInfo.isLoggedIn]);

    // 메인 "/" 표시용: 유효 토큰 있으면 localStorage에서 meInfo 사용 (id 있을 때만 유효로 간주)
    const hasToken = readStoredAuth().isLoggedIn;
    const rawStored = hasToken ? getMeInfoFromStorage() : null;
    const effectiveMeInfo = (meInfo?.id ? meInfo : (rawStored?.id ? rawStored : null)) || null;

    useEffect(() => {
        if (effectiveMeInfo && meInfo === null) loadMeInfo();
    }, [authInfo.isLoggedIn, effectiveMeInfo?.id, meInfo]);

    // meInfoChanged / meInfoSynced 이벤트 리스닝 (SNS 로그인 직후 메인 "로딩 중..." 방지)
    useEffect(() => {
        const handleMeInfoChanged = () => loadMeInfo();
        const handleMeInfoSynced = (e) => e?.detail && setMeInfo(e.detail);

        window.addEventListener('meInfoChanged', handleMeInfoChanged);
        window.addEventListener('meInfoSynced', handleMeInfoSynced);

        return () => {
            window.removeEventListener('meInfoChanged', handleMeInfoChanged);
            window.removeEventListener('meInfoSynced', handleMeInfoSynced);
        };
    }, []);

    // 주기적으로 localStorage 확인 (카카오 로그인 후 리다이렉트 대응)
    useEffect(() => {
        let checkCount = 0;
        const maxChecks = 10; // 최대 10번 확인 (5초)

        const checkMeInfo = () => {
            checkCount++;
            const storedMeInfo = JSON.parse(localStorage.getItem('meInfo') || '{}');
            const currentMeInfoId = meInfo?.id;

            // localStorage에 meInfo가 있고, 현재 meInfo와 다르면 다시 로드
            if (storedMeInfo?.id && storedMeInfo.id !== currentMeInfoId) {
                loadMeInfo();
                return; // 발견했으면 더 이상 확인하지 않음
            }

            // 아직 확인 횟수가 남았으면 계속 확인
            if (checkCount < maxChecks) {
                setTimeout(checkMeInfo, 500);
            }
        };

        // 즉시 확인 시작
        checkMeInfo();
    }, [meInfo?.id]); // 컴포넌트 마운트 시 한 번만 실행

    // /auth/* 는 Content 래퍼 없이 렌더되므로 #root·.App 의 overflow:hidden 에서 잘림.
    // 이 경로에서만 문서 높이에 맞춰 스크롤되도록 셸 클래스를 토글한다.
    const isDocumentScrollShell =
        location.pathname.startsWith('/auth');

    useEffect(() => {
        const root = document.getElementById('root');
        if (!root) return;
        const cls = 'App-root--documentScroll';
        if (isDocumentScrollShell) {
            root.classList.add(cls);
        } else {
            root.classList.remove(cls);
        }
        return () => root.classList.remove(cls);
    }, [isDocumentScrollShell]);

    return (
        <div
            className={`App${isDocumentScrollShell ? ' App--documentScroll' : ''}`}
            style={{
                height: '100dvh', // dynamic viewport height (모바일 브라우저 navigation bar 고려)
                minHeight: '100dvh',
                overflow: 'hidden',
                backgroundColor: backgroundColor,
                paddingBottom: 'env(safe-area-inset-bottom, 0px)', // 모바일에서 safe area 고려
            }}
        >
            <LevelsProvider>
                <CssBaseline />

                <Routes>
                    <Route
                        path="/auth/*"
                        element={
                            <>
                                <Routes>
                                    <Route
                                        path="signin"
                                        element={<SignIn />}
                                    />
                                    <Route
                                        path="signup"
                                        element={<SignUp />}
                                    />
                                    <Route
                                        path="organization-search"
                                        element={<OrganizationSearch />}
                                    />
                                    <Route
                                        path="organization-search-detail"
                                        element={<OrganizationSearchDetail />}
                                    />
                                    <Route
                                        path="reset-password"
                                        element={<ResetPassword />}
                                    />
                                    <Route
                                        path="terms"
                                        element={<Terms />}
                                    />
                                    <Route
                                        path="language"
                                        element={<Language />}
                                    />
                                    <Route
                                        path="organization-apply"
                                        element={<OrganizationApply />}
                                    />
                                </Routes>
                            </>
                        }
                    />

                    {/* Protected Routes */}
                    <Route
                        path="/*"
                        element={
                            <Auth>
                                {authInfo.isLoggedIn ? (
                                    <>
                                        {isHeaderRoute && (
                                            <Header
                                                appType={appType}
                                                setMenusWidth={
                                                    setMenusWidth
                                                }
                                                setPageWidth={setPageWidth}
                                                isMenuOpen={isMenuOpen}
                                                setIsMenuOpen={
                                                    setIsMenuOpen
                                                }
                                            />
                                        )}
                                        <Content
                                            menusWidth={
                                                isHeaderRoute
                                                    ? menusWidth
                                                    : 0
                                            }
                                            pageWidth={
                                                isHeaderRoute
                                                    ? pageWidth
                                                    : window.innerWidth
                                            }
                                            padding={
                                                location.pathname.startsWith('/championships/user-detail') ||
                                                    location.pathname.startsWith('/reports/activity') ||
                                                    location.pathname.startsWith('/reports/didim')
                                                    ? '0'
                                                    : '0 10px 10px 10px'
                                            }
                                            appType={appType}
                                        >
                                            <Routes>
                                                <Route
                                                    path="/"
                                                    element={
                                                        !effectiveMeInfo?.id ? (
                                                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                                                <Typography>로딩 중...</Typography>
                                                            </Box>
                                                        ) : isSupervisor(effectiveMeInfo.grade) ? (
                                                            <OrganizationList />
                                                        ) : isAdmin(effectiveMeInfo.grade) ? (
                                                            <OrganizationDetail />
                                                        ) : (
                                                            <UserMain />
                                                        )
                                                    }
                                                />

                                                <Route path="/applications">
                                                    <Route
                                                        index
                                                        element={
                                                            <ApplicationList />
                                                        }
                                                    />
                                                    <Route
                                                        path="list"
                                                        element={
                                                            <ApplicationList />
                                                        }
                                                    />
                                                    <Route
                                                        path="detail"
                                                        element={
                                                            <ApplicationDetail />
                                                        }
                                                    />
                                                </Route>

                                                <Route path="/contents">
                                                    <Route
                                                        index
                                                        element={
                                                            <ContentsList />
                                                        }
                                                    />
                                                    <Route
                                                        path="list"
                                                        element={
                                                            <ContentsList />
                                                        }
                                                    />
                                                    <Route
                                                        path="edit"
                                                        element={
                                                            <ContentsEdit />
                                                        }
                                                    />
                                                </Route>

                                                <Route path="/didims">
                                                    <Route
                                                        index
                                                        element={
                                                            <DidimList />
                                                        }
                                                    />
                                                    <Route
                                                        path="list"
                                                        element={
                                                            <DidimList />
                                                        }
                                                    />
                                                    <Route
                                                        path="edit"
                                                        element={
                                                            <DidimEdit />
                                                        }
                                                    />
                                                </Route>

                                                <Route path="/events">
                                                    <Route
                                                        index
                                                        element={isUser(effectiveMeInfo?.grade) ? <EventUser /> : <EventAdmin />}
                                                    />
                                                    <Route
                                                        path="admin"
                                                        element={
                                                            <EventAdmin />
                                                        }
                                                    />
                                                    <Route
                                                        path="admin-detail"
                                                        element={
                                                            <EventAdminDetail />
                                                        }
                                                    />
                                                    <Route
                                                        path="edit"
                                                        element={
                                                            <EventEdit />
                                                        }
                                                    />
                                                    <Route
                                                        path="user"
                                                        element={
                                                            <EventUser />
                                                        }
                                                    />
                                                    <Route
                                                        path="user-detail"
                                                        element={
                                                            <EventUserDetail />
                                                        }
                                                    />
                                                </Route>

                                                <Route path="/generals">
                                                    <Route
                                                        index
                                                        element={
                                                            <GeneralList />
                                                        }
                                                    />
                                                    <Route
                                                        path="list"
                                                        element={
                                                            <GeneralList />
                                                        }
                                                    />
                                                    <Route
                                                        path="edit"
                                                        element={
                                                            <GeneralEdit />
                                                        }
                                                    />
                                                </Route>

                                                <Route path="/organizations">
                                                    <Route
                                                        index
                                                        element={
                                                            <OrganizationList />
                                                        }
                                                    />
                                                    <Route
                                                        path="list"
                                                        element={
                                                            <OrganizationList />
                                                        }
                                                    />
                                                    <Route
                                                        path="detail"
                                                        element={
                                                            <OrganizationDetail />
                                                        }
                                                    />
                                                    <Route
                                                        path="edit"
                                                        element={
                                                            <OrganizationUpdate />
                                                        }
                                                    />
                                                </Route>

                                                <Route path="/reports">
                                                    <Route
                                                        index
                                                        element={
                                                            <ReportActivity />
                                                        }
                                                    />
                                                    <Route
                                                        path="activity"
                                                        element={
                                                            <ReportActivity />
                                                        }
                                                    />
                                                    <Route
                                                        path="activity-monthly"
                                                        element={
                                                            <ReportActivityMonthly />
                                                        }
                                                    />
                                                    <Route
                                                        path="step"
                                                        element={
                                                            <ReportStep />
                                                        }
                                                    />
                                                    <Route
                                                        path="step-union"
                                                        element={
                                                            <ReportStepUnion />
                                                        }
                                                    />
                                                    <Route
                                                        path="heartbeat"
                                                        element={
                                                            <ReportHeartbeat />
                                                        }
                                                    />
                                                    <Route
                                                        path="heartbeat-union"
                                                        element={
                                                            <ReportHeartbeatUnion />
                                                        }
                                                    />
                                                    <Route
                                                        path="didim"
                                                        element={
                                                            <ReportDidim />
                                                        }
                                                    />
                                                    <Route
                                                        path="didim-monthly"
                                                        element={
                                                            <ReportDidimMonthly />
                                                        }
                                                    />
                                                </Route>

                                                <Route path="/rewards">
                                                    <Route
                                                        index
                                                        element={
                                                            <RewardChallenge />
                                                        }
                                                    />
                                                    <Route path="challenge"
                                                        element={
                                                            <RewardChallenge />
                                                        }
                                                    />
                                                    <Route
                                                        path="history"
                                                        element={
                                                            <RewardHistory />
                                                        }
                                                    />
                                                </Route>

                                                <Route path="/championships">
                                                    <Route
                                                        index
                                                        element={
                                                            isUser(effectiveMeInfo?.grade) ? <ChampionshipUser /> : <ChampionshipAdmin />
                                                        }
                                                    />
                                                    <Route
                                                        path="user"
                                                        element={
                                                            <ChampionshipUser />
                                                        }
                                                    />
                                                    <Route
                                                        path="user-detail"
                                                        element={
                                                            <ChampionshipUserDetail />
                                                        }
                                                    />
                                                    <Route
                                                        path="user-add"
                                                        element={
                                                            <ChampionshipUserAdd />
                                                        }
                                                    />
                                                    <Route
                                                        path="content-selection"
                                                        element={
                                                            <ContentSelection />
                                                        }
                                                    />
                                                    <Route
                                                        path="admin"
                                                        element={
                                                            <ChampionshipAdmin />
                                                        }
                                                    />
                                                    <Route
                                                        path="admin-add"
                                                        element={
                                                            <ChampionshipAdminAdd />
                                                        }
                                                    />
                                                </Route>

                                                <Route path="/users">
                                                    <Route
                                                        index
                                                        element={
                                                            <UserList />
                                                        }
                                                    />
                                                    <Route
                                                        path="edit"
                                                        element={
                                                            <UserEdit />
                                                        }
                                                    />
                                                    <Route
                                                        path="detail"
                                                        element={
                                                            <UserDetail />
                                                        }
                                                    />
                                                    <Route
                                                        path="mypage"
                                                        element={
                                                            <UserMypage />
                                                        }
                                                    />
                                                    <Route
                                                        path="mypage-edit"
                                                        element={
                                                            <UserMypageEdit />
                                                        }
                                                    />
                                                    <Route
                                                        path="exp"
                                                        element={
                                                            <UserExp />
                                                        }
                                                    />
                                                    <Route
                                                        path="membership"
                                                        element={
                                                            <UserMembership />
                                                        }
                                                    />
                                                    <Route
                                                        path="qr"
                                                        element={
                                                            <UserQr />
                                                        }
                                                    />
                                                    <Route
                                                        path="reset-password"
                                                        element={
                                                            <ResetPassword />
                                                        }
                                                    />
                                                    {/* 메인은 "/"로 통일 (앱 백/히스토리와 동일한 URL) */}
                                                    <Route
                                                        path="main"
                                                        element={<Navigate to="/" replace />}
                                                    />
                                                    <Route
                                                        path="push"
                                                        element={
                                                            <UserPush />
                                                        }
                                                    />
                                                    <Route
                                                        path="language"
                                                        element={
                                                            <Language />
                                                        }
                                                    />
                                                </Route>

                                                <Route path="/notices">
                                                    <Route
                                                        index
                                                        element={
                                                            isUser(effectiveMeInfo?.grade) ? (
                                                                <NoticeList />
                                                            ) : (
                                                                <NoticeAdmin />
                                                            )
                                                        }
                                                    />
                                                    <Route
                                                        path="notice-list"
                                                        element={
                                                            <NoticeList />
                                                        }
                                                    />
                                                    <Route
                                                        path="notice-detail"
                                                        element={
                                                            <NoticeDetail />
                                                        }
                                                    />
                                                    <Route
                                                        path="notification-list"
                                                        element={
                                                            <NoticeNotification />
                                                        }
                                                    />
                                                </Route>

                                                <Route
                                                    path="reset-password"
                                                    element={
                                                        <ResetPassword />
                                                    }
                                                />
                                            </Routes>
                                        </Content>
                                        <Outlet />
                                        <Footer menusWidth={menusWidth} appType={appType} />
                                    </>
                                ) : (
                                    <Routes>
                                        <Route
                                            path="*"
                                            element={<SignIn />}
                                        />
                                    </Routes>
                                )}
                            </Auth>
                        }
                    />
                </Routes>
            </LevelsProvider>
        </div >
    );
}

export default App;
